import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { getAchievements } from '../api/achievements';
import BadgeMedallion from '../components/BadgeMedallion';
import BadgeDetailModal from '../components/BadgeDetailModal';
import { CATEGORY_LABELS, CATEGORY_ORDER, familyLabel } from '../constants/achievements';
import '../styles/AchievementsPage.css';

/** How many "Up Next" row cards to show before the "Show more" toggle. */
const UP_NEXT_CAP = 6;

/** How many recently earned badges to preview in the collection summary. */
const RECENT_CAP = 5;

/**
 * Collapse the flat API list into badges: one per tiered family (using the
 * `family` field the API computes from `_TIERED_PREFIXES`), one per standalone
 * achievement. A family badge reports its highest earned tier and the progress
 * toward the next one.
 */
const buildBadges = (achievements) => {
    const families = new Map();
    const badges = [];

    achievements.forEach((a) => {
        if (!a.family) {
            badges.push({
                key: a.id,
                family: null,
                name: a.name,
                description: a.description,
                icon: a.icon,
                category: a.category || 'other',
                recurrence: a.recurrence,
                earned: a.earned,
                complete: a.earned,
                tierIndex: 0,
                earnedCount: a.earned ? 1 : 0,
                totalCount: 1,
                progress: a.progress ?? null,
                progressTarget: a.progress_target ?? null,
                spudcoinReward: a.spudcoin_reward,
                earnedAt: a.earned_at,
                tiers: [a],
            });
            return;
        }
        if (!families.has(a.family)) {
            const badge = { family: a.family, tiers: [] };
            families.set(a.family, badge);
            badges.push(badge);
        }
        families.get(a.family).tiers.push(a);
    });

    families.forEach((badge) => {
        const tiers = badge.tiers.sort((x, y) => (x.tier ?? 0) - (y.tier ?? 0));

        let tierIndex = -1;
        tiers.forEach((tier, index) => {
            if (tier.earned) tierIndex = index;
        });
        const nextTier = tiers.find((tier) => !tier.earned) || null;
        const representative = tiers[Math.max(tierIndex, 0)];
        const earnedCount = tiers.filter((tier) => tier.earned).length;

        Object.assign(badge, {
            key: `family:${badge.family}`,
            name: familyLabel(badge.family),
            description: (nextTier || tiers[tiers.length - 1]).description,
            icon: representative.icon,
            category: tiers[0].category || 'other',
            recurrence: 'once',
            earned: earnedCount > 0,
            complete: nextTier === null,
            tierIndex: Math.max(tierIndex, 0),
            earnedCount,
            totalCount: tiers.length,
            progress: nextTier ? nextTier.progress ?? null : null,
            progressTarget: nextTier ? nextTier.progress_target ?? null : null,
            spudcoinReward: (nextTier || tiers[tiers.length - 1]).spudcoin_reward,
            earnedAt: tierIndex >= 0 ? tiers[tierIndex].earned_at : null,
        });
    });

    return badges;
};

const percentOf = (badge) =>
    badge.progress != null && badge.progressTarget
        ? Math.min(100, Math.round((badge.progress / badge.progressTarget) * 100))
        : 0;

/**
 * Badges worth surfacing as full row cards: anything unfinished that the user can
 * measurably move on, plus daily/weekly achievements still available this period.
 * Time-limited ones sort first, then whatever is closest to completion.
 */
const buildUpNext = (badges) =>
    badges
        .filter((badge) => {
            if (badge.complete) return false;
            if (badge.recurrence !== 'once') return true;
            return badge.progress != null && badge.progressTarget != null;
        })
        .sort((a, b) => {
            const recurring = (badge) => (badge.recurrence !== 'once' ? 0 : 1);
            if (recurring(a) !== recurring(b)) return recurring(a) - recurring(b);
            return percentOf(b) - percentOf(a);
        });

const AchievementsPage = () => {
    const history = useHistory();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeCategory, setActiveCategory] = useState('all');
    const [showAllUpNext, setShowAllUpNext] = useState(false);
    const [selectedBadge, setSelectedBadge] = useState(null);

    useEffect(() => {
        let mounted = true;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const result = await getAchievements();
                if (mounted) setData(result);
            } catch (err) {
                if (mounted) setError(err.message || 'Failed to load achievements');
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, []);

    const achievements = useMemo(() => data?.achievements || [], [data]);
    const badges = useMemo(() => buildBadges(achievements), [achievements]);
    const upNext = useMemo(() => buildUpNext(badges), [badges]);

    const categories = useMemo(() => {
        const present = new Set(badges.map((badge) => badge.category));
        return [
            ...CATEGORY_ORDER.filter((category) => present.has(category)),
            ...[...present].filter((category) => !CATEGORY_ORDER.includes(category)).sort(),
        ];
    }, [badges]);

    const visibleBadges = useMemo(
        () => (activeCategory === 'all'
            ? badges
            : badges.filter((badge) => badge.category === activeCategory)),
        [badges, activeCategory],
    );

    const recentBadges = useMemo(
        () => badges
            .filter((badge) => badge.earned && badge.earnedAt)
            .sort((a, b) => new Date(b.earnedAt) - new Date(a.earnedAt))
            .slice(0, RECENT_CAP),
        [badges],
    );

    if (loading) {
        return (
            <div className="ach-page">
                <div className="ach-container">
                    <div className="ach-loading">
                        <div className="ach-spinner"></div>
                        <p>Loading achievements...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="ach-page">
                <div className="ach-container">
                    <div className="ach-error">
                        <span>⚠️</span>
                        <p>{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    const totalEarned = data?.total_earned ?? 0;
    const totalAvailable = data?.total_available ?? 0;
    const completionPct = totalAvailable
        ? Math.min(100, Math.round((totalEarned / totalAvailable) * 100))
        : 0;

    const shownUpNext = showAllUpNext ? upNext : upNext.slice(0, UP_NEXT_CAP);

    const countFor = (category) => {
        const scoped = category === 'all'
            ? badges
            : badges.filter((badge) => badge.category === category);
        return `${scoped.filter((badge) => badge.earned).length}/${scoped.length}`;
    };

    return (
        <div className="ach-page">
            <div className="ach-container">

                {/* Header — the SpudCoin balance doubles as the entrance to the SpudMarket */}
                <div className="ach-header">
                    <div>
                        <h2>Achievements</h2>
                        <p className="ach-header-sub">Earn SpudCoin by hitting milestones</p>
                    </div>
                    <button
                        type="button"
                        className="ach-coin-tile"
                        onClick={() => history.push('/spudmarket')}
                        aria-label={`${data?.spudcoin_balance ?? 0} SpudCoin. Open the SpudMarket.`}
                    >
                        <span className="ach-coin-icon">🪙</span>
                        <span className="ach-coin-text">
                            <span className="ach-coin-value">{data?.spudcoin_balance ?? 0}</span>
                            <span className="ach-coin-label">Spend in SpudMarket</span>
                        </span>
                        <span className="ach-coin-arrow" aria-hidden="true">›</span>
                    </button>
                </div>

                {/* Collection summary — completion ring plus the most recent badges */}
                <div className="ach-summary">
                    <div
                        className="ach-summary-ring"
                        style={{
                            background: `conic-gradient(var(--color-primary) ${completionPct}%, var(--color-primary-track) 0)`,
                        }}
                    >
                        <div className="ach-summary-ring-face">
                            <span className="ach-summary-pct">{completionPct}%</span>
                        </div>
                    </div>
                    <div className="ach-summary-text">
                        <h3>{totalEarned}/{totalAvailable}</h3>
                        <p>Unlocked</p>
                    </div>
                    {recentBadges.length > 0 && (
                        <div className="ach-summary-recent">
                            <span className="ach-summary-recent-label">Recently earned</span>
                            <div className="ach-summary-recent-row">
                                {recentBadges.map((badge) => (
                                    <button
                                        key={badge.key}
                                        type="button"
                                        className="ach-summary-recent-badge"
                                        onClick={() => setSelectedBadge(badge)}
                                        aria-label={badge.name}
                                        title={badge.name}
                                    >
                                        {badge.icon || '🏆'}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="ach-content">

                    {/* Up Next — bounded list of what the user can act on right now */}
                    {shownUpNext.length > 0 && (
                        <div className="ach-section">
                            <p className="ach-section-title">Up Next</p>
                            <div className="ach-list">
                                {shownUpNext.map((badge) => {
                                    const isDaily = badge.recurrence === 'daily';
                                    const isWeekly = badge.recurrence === 'weekly';
                                    const showProgress =
                                        badge.progress != null && badge.progressTarget != null;
                                    const pct = percentOf(badge);

                                    return (
                                        <button
                                            key={badge.key}
                                            type="button"
                                            className={`ach-card ${isDaily ? 'daily' : isWeekly ? 'weekly' : ''}`}
                                            onClick={() => setSelectedBadge(badge)}
                                        >
                                            <div className="ach-card-icon-wrap">{badge.icon || '🏆'}</div>

                                            <div className="ach-card-body">
                                                <div className="ach-card-name-row">
                                                    <div className="ach-card-name">{badge.name}</div>
                                                    {isDaily && <span className="ach-daily-badge">Daily</span>}
                                                    {isWeekly && <span className="ach-weekly-badge">Weekly</span>}
                                                    {badge.family && (
                                                        <span className="ach-tier-badge">
                                                            {badge.earnedCount}/{badge.totalCount}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="ach-card-desc">{badge.description}</div>
                                                {showProgress && (
                                                    <div className="ach-progress">
                                                        <div className="ach-progress-track">
                                                            <div
                                                                className="ach-progress-fill"
                                                                style={{ width: `${pct}%` }}
                                                            />
                                                        </div>
                                                        <span className="ach-progress-label">
                                                            {badge.progress}/{badge.progressTarget}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="ach-card-right">
                                                <span className="ach-reward ach-reward--locked">
                                                    🪙 {badge.spudcoinReward}
                                                </span>
                                                {isDaily && (
                                                    <span className="ach-daily-available">Available today</span>
                                                )}
                                                {isWeekly && (
                                                    <span className="ach-weekly-available">Available this week</span>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                            {upNext.length > UP_NEXT_CAP && (
                                <button
                                    type="button"
                                    className="ach-show-more"
                                    onClick={() => setShowAllUpNext((prev) => !prev)}
                                >
                                    {showAllUpNext
                                        ? 'Show less'
                                        : `Show ${upNext.length - UP_NEXT_CAP} more`}
                                </button>
                            )}
                        </div>
                    )}

                    {/* Collection — every badge, filterable by category */}
                    <div className="ach-section">
                        <p className="ach-section-title">Collection</p>

                        <div className="ach-chips" role="tablist" aria-label="Filter badges by category">
                            {['all', ...categories].map((category) => (
                                <button
                                    key={category}
                                    type="button"
                                    role="tab"
                                    aria-selected={activeCategory === category}
                                    className={`ach-chip ${activeCategory === category ? 'is-active' : ''}`}
                                    onClick={() => setActiveCategory(category)}
                                >
                                    {category === 'all'
                                        ? 'All'
                                        : CATEGORY_LABELS[category] || category}
                                    <span className="ach-chip-count">{countFor(category)}</span>
                                </button>
                            ))}
                        </div>

                        {visibleBadges.length > 0 ? (
                            <div className="ach-grid">
                                {visibleBadges.map((badge) => (
                                    <BadgeMedallion
                                        key={badge.key}
                                        badge={badge}
                                        onClick={() => setSelectedBadge(badge)}
                                    />
                                ))}
                            </div>
                        ) : (
                            <p className="ach-empty">No badges in this category yet.</p>
                        )}
                    </div>

                </div>
            </div>

            {selectedBadge && (
                <BadgeDetailModal
                    badge={selectedBadge}
                    onClose={() => setSelectedBadge(null)}
                />
            )}
        </div>
    );
};

export default AchievementsPage;
